import { Controller } from '@hotwired/stimulus'
import { lastLoadedItem, PRODUCT_PREFIX } from '../utils/grid_items'

// This controller allows to throttle a Turbo frame based
// pagination system. The frame used to trigger the next
// page load is initially disabled. When the system is ready,
// in particular when masonry has finished its rendering magic,
// it can be re-enabled.
export default class extends Controller {
  static targets = ['frame', 'items', 'scope']

  static values = {
    bottomMargin: { type: Number, default: 1000 },
  }

  connect() {
    this.isLoading = false
    this.ready = false
  }

  // Le flux de pagination remplace la frame à chaque page, et elle arrive désactivée.
  // L'ordre des deux actions du flux (ajout des cartes, remplacement de la frame) n'est
  // pas garanti : le signal d'activation peut partir avant que la nouvelle frame soit
  // là. On la réactive donc aussi à sa connexion, une fois la grille prête.
  frameTargetConnected(frame) {
    if (!this.ready) return

    // On réactive seulement : déclencher le chargement ici aussi ferait partir deux
    // requêtes pour la même position (page ajoutée en double). Le prochain événement
    // de défilement, ou le signal de la grille, s'en charge.
    this.isLoading = false
    frame.disabled = false
  }

  enableBeaconTargetConnected(beaconElement) {
    this.enable()
    beaconElement.remove()
  }

  enable() {
    this.ready = true
    this.isLoading = false

    if (this.hasFrameTarget) {
      this.frameTarget.disabled = false
      this.load()
    }
  }

  load() {
    if (this.isLoading || !this.hasFrameTarget) return

    if (this.#mustTrigger()) {
      this.isLoading = true
      this.frameTarget.loading = 'eager'
    }
  }

  // Grille d'une liste : la page suivante part du dernier produit affiché, lu au moment où la
  // requête part — et non d'un numéro de page, que les pages chargées entre-temps démentent dès
  // qu'une envie est retirée ou ajoutée au-dessus (cf. Lists::ProductsLoader#after_product).
  // Toute requête partie de la grille passe par ici (suppression, favori, section…) : sans frame,
  // liste courte ou dernière page chargée, il n'y a rien à faire.
  stampCursor(event) {
    if (!this.hasFrameTarget || event.target !== this.frameTarget) return

    const last = lastLoadedItem(this.itemsTarget, { productsOnly: true })
    if (last) event.detail.url.searchParams.set('after', last.id.slice(PRODUCT_PREFIX.length))
  }

  #mustTrigger() {
    // Grille masquée (celle de la fiche produit, réservée au desktop) : sa hauteur nulle
    // la disait toujours arrivée en bas, et elle chargeait toutes ses pages en fond.
    if (this.itemsTarget.offsetParent === null) return false

    let scopeBottom
    const itemsBottom = this.itemsTarget.offsetTop + this.itemsTarget.offsetHeight

    if (this.hasScopeTarget) {
      scopeBottom = this.scopeTarget.scrollTop + this.scopeTarget.offsetHeight
    } else {
      scopeBottom = window.scrollY + window.innerHeight
    }

    return scopeBottom > itemsBottom - this.bottomMarginValue
  }
}
